import React, {useMemo} from 'react'
import {Box as MuiBox, MenuItem, ToggleButton, ToggleButtonGroup, Tooltip} from '@mui/material'
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {TimePicker} from '@mui/x-date-pickers/TimePicker'
import sv from 'date-fns/locale/sv'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import {useTheme} from '@mui/material/styles'
import {useForm} from '../../../common/hooks/useForm'
import {useNotification} from '../../../common/notifications/useNotification'
import {Box as BoxType, BoxColor} from '../../../common/types/box'
import {Location} from '../../../common/types/location'
import {resolveCoordinates} from '../../../common/utils/resolveCoordinates'
import {GQL_ContractType, GQL_EditBoxResponseType, Maybe, useEditBoxMutation} from '../../../generated/graphql'
import {transformBoxColorToGQL} from '../../../gql/transformBox'
import {Box} from '../../../webui/Box'
import {Button} from '../../../webui/Button'
import {SaveButton} from '../../../webui/buttons/SaveButton'
import {Drawer} from '../../../webui/Drawer'
import {CheckboxWithLabel, CheckboxWithLabelProps} from '../../../webui/inputs/CheckboxWithLabel'
import {Select, SelectChangeEvent, SelectInputProps} from '../../../webui/inputs/Select'
import {TextField, TextFieldProps} from '../../../webui/inputs/TextField'
import {Typography} from '../../../webui/Typography'
import {LocationTranslationsForm} from '../../location/add/AddLocation'
import {buildLocationTranslationsInput} from '../../location/add/buildLocationTranslationsInput'
import {EditLocation, EditLocationForm} from '../../location/edit/EditLocation'
import {NON_CANONICAL_LOCATION_TRANSLATION_LANGUAGES, NonCanonicalLocationTranslationLanguage} from '../../location/translationLanguages'
import {PriceWeightSelect} from '../../price/PriceWeightSelect'
import {SponsorSelect} from '../../sponsor/SponsorSelect'
import {BoxColorSelect} from '../BoxColorSelect'
import {DeleteBoxButton} from '../delete/DeleteBoxButton'
import {useEditFormLocationDescription} from '../utils/useTranslatedLocation'
import {contractTypeLabel} from '../../../common/domain/contractType'
import {classifyOpeningWindow, dateToHHmm, hhmmToDate, hhmmToIsoStockholm, isoToHHmmStockholm} from '../utils/openingHours'
import {OpeningHoursPreview} from './OpeningHoursPreview'
import {FormSection} from './FormSection'

interface EditBoxDrawerProps {
  box: BoxType
  open: boolean
  onClose: () => void
}

interface EditBoxForm {
  active: boolean
  temporarilyClosed: boolean
  circuitBoardId: string
  color: BoxColor
  contractType: '' | GQL_ContractType
  deprecated: boolean
  name: string
  alwaysOpen: boolean
  openStartTime: string
  openEndTime: string
  priceWeight: string
  sponsorId: string
}

// When "Always open" is selected, the box is open 24h; the app treats equal
// start/end times as all-day, so that is what we persist.
const ALWAYS_OPEN_TIME = '00:00'
const DEFAULT_OPEN_START = '08:00'
const DEFAULT_OPEN_END = '18:00'

interface LabelWithTooltipProps {
  label: string
  tooltip: string
  color?: string
}

const LabelWithTooltip: React.FC<LabelWithTooltipProps> = ({label, tooltip, color}) => (
  <MuiBox display="flex" alignItems="center" gap={0.5}>
    <span style={{color, fontWeight: color ? 500 : undefined}}>{label}</span>
    <Tooltip title={tooltip} arrow>
      <HelpOutlineIcon
        fontSize="small"
        sx={{
          opacity: 0.65,
          cursor: 'help',
          fontSize: 16,
          verticalAlign: 'middle',
        }}
      />
    </Tooltip>
  </MuiBox>
)

const createInitialEditBoxForm = (box: BoxType): EditBoxForm => {
  const start = isoToHHmmStockholm(box.openStartTime)
  const end = isoToHHmmStockholm(box.openEndTime)
  const kind = classifyOpeningWindow(start, end)
  const hasSpecificHours = kind === 'same-day' || kind === 'overnight'
  return {
    active: box.active,
    temporarilyClosed: box.temporarilyClosed,
    circuitBoardId: box.circuitBoardId,
    color: box.color,
    contractType: box.contractType ?? '',
    deprecated: box.deprecated,
    name: box.name,
    alwaysOpen: !hasSpecificHours,
    // Keep editable values for specific-hours mode; fall back to sensible defaults
    // so toggling into "Specific hours" never shows empty inputs.
    openStartTime: hasSpecificHours ? start : DEFAULT_OPEN_START,
    openEndTime: hasSpecificHours ? end : DEFAULT_OPEN_END,
    priceWeight: box.priceWeight.id,
    sponsorId: box.sponsor?.id ?? '',
  }
}

const createInitialEditLocationForm = (
  location: Location | undefined,
  resolveDescription: (location: Location, boxName: string, lng: NonCanonicalLocationTranslationLanguage | 'en') => string,
  boxName: string,
): EditLocationForm => {
  if (location) {
    const translations: LocationTranslationsForm = NON_CANONICAL_LOCATION_TRANSLATION_LANGUAGES.reduce(
      (acc, lang) => ({...acc, [lang]: {description: resolveDescription(location, boxName, lang)}}),
      {} as LocationTranslationsForm,
    )
    return {
      name: location.name,
      description: resolveDescription(location, boxName, 'en'),
      lat: location.coordinates?.lat ? String(location.coordinates.lat) : undefined,
      long: location.coordinates?.long ? String(location.coordinates.long) : undefined,
      locality: location.locality,
      municipality: location.municipality,
      translations,
    }
  }
  return {}
}

const resolveErrorMessage = (type: Maybe<GQL_EditBoxResponseType> | undefined): string => {
  switch (type) {
    case GQL_EditBoxResponseType.NoPermissionToEditBox:
      return 'No permission to edit box'
    case GQL_EditBoxResponseType.BoxDoesNotExist:
      return 'Box does not exist'
  }
  return 'Could not edit box'
}

export const EditBoxDrawer: React.FC<EditBoxDrawerProps> = ({box, open, onClose}) => {
  const theme = useTheme()

  const {form, updateField, updateFields, reset} = useForm(
    useMemo(
      () => createInitialEditBoxForm(box),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        box.id,
        box.active,
        box.temporarilyClosed,
        box.circuitBoardId,
        box.color,
        box.contractType,
        box.deprecated,
        box.name,
        box.openStartTime,
        box.openEndTime,
        box.priceWeight.id,
        box.sponsor?.id,
      ],
    ),
  )
  const resolveLocationDescription = useEditFormLocationDescription()
  const locationForm = useForm(
    useMemo(
      () => createInitialEditLocationForm(box.location, resolveLocationDescription, box.name),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        box.location?.id,
        box.location?.name,
        box.location?.description,
        box.location?.locality,
        box.location?.municipality,
        box.location?.coordinates?.lat,
        box.location?.coordinates?.long,
        box.location?.translations,
        box.name,
      ],
    ),
  )

  const {showNotification} = useNotification()
  const [editBox, {loading}] = useEditBoxMutation()

  const onNameChange: TextFieldProps['onChange'] = (value) => {
    updateField('name', value)
  }
  const onActiveChange: CheckboxWithLabelProps['onChange'] = (value) => {
    updateField('active', value)
  }
  const onTemporarilyClosedChange: CheckboxWithLabelProps['onChange'] = (value) => {
    updateField('temporarilyClosed', value)
  }
  const onDeprecatedChange: CheckboxWithLabelProps['onChange'] = (value) => {
    updateField('deprecated', value)
  }
  const onAvailabilityModeChange = (_e: React.MouseEvent<HTMLElement>, value: 'always' | 'specific' | null) => {
    if (value === null) return // ignore deselecting the active option
    if (value === 'specific') {
      updateFields({
        alwaysOpen: false,
        openStartTime: form.openStartTime || DEFAULT_OPEN_START,
        openEndTime: form.openEndTime || DEFAULT_OPEN_END,
      })
    } else {
      updateField('alwaysOpen', true)
    }
  }
  const onOpenStartTimeChange = (date: Date | null) => {
    updateField('openStartTime', dateToHHmm(date))
  }
  const onOpenEndTimeChange = (date: Date | null) => {
    updateField('openEndTime', dateToHHmm(date))
  }
  const onCircuitBoardIdChange: TextFieldProps['onChange'] = (value) => {
    updateField('circuitBoardId', value)
  }
  const onColorChange = (value: BoxColor) => {
    updateField('color', value)
  }
  const onSponsorChange = (value: string) => {
    updateField('sponsorId', value)
  }
  const onPriceWeightChange: SelectInputProps<string>['onChange'] = (e) => {
    updateField('priceWeight', e.target.value as string)
  }

  const onContractTypeChange = (e: SelectChangeEvent<GQL_ContractType>) => {
    const value = e.target.value as unknown as '' | GQL_ContractType
    updateField('contractType', value)
  }

  const onCancel = () => {
    reset()
    onClose()
  }

  // "Always open" persists as equal start/end (the app's all-day convention).
  // Otherwise use the specific times the admin entered.
  const openStartTime = form.alwaysOpen ? ALWAYS_OPEN_TIME : form.openStartTime
  const openEndTime = form.alwaysOpen ? ALWAYS_OPEN_TIME : form.openEndTime

  const onSave = async () => {
    // In specific-hours mode both times are required. Overnight (start > end) and
    // all-day (start == end) windows are valid and must NOT be rejected.
    if (!form.alwaysOpen && (!form.openStartTime || !form.openEndTime)) {
      showNotification({message: 'Set both open start and end time, or choose "Always open".', severity: 'error'})
      return
    }

    try {
      const translations = locationForm.form.translations
        ? buildLocationTranslationsInput(locationForm.form.description ?? '', locationForm.form.translations)
        : undefined
      const loc = {
        coordinates: resolveCoordinates(locationForm.form.lat, locationForm.form.long),
        description: locationForm.form.description,
        locality: locationForm.form.locality,
        municipality: locationForm.form.municipality,
        name: locationForm.form.name,
        translations,
      }

      const result = await editBox({
        variables: {
          boxId: box.id,
          boxInput: {
            location: loc,
            active: form.active,
            temporarilyClosed: form.temporarilyClosed,
            circuitBoardId: form.circuitBoardId,
            color: transformBoxColorToGQL(form.color),
            contractType: form.contractType ? form.contractType : null,
            deprecated: form.deprecated,
            name: form.name,
            openStartTime: hhmmToIsoStockholm(openStartTime),
            openEndTime: hhmmToIsoStockholm(openEndTime),
            priceWeight: form.priceWeight || undefined,
            sponsorId: form.sponsorId,
          },
        },
        update(cache, {data}) {
          const type = data?.editBox?.type ?? undefined
          if (type !== 'Success') return

          const cacheId = cache.identify({__typename: 'Box', id: box.id})
          if (!cacheId) return

          cache.modify({
            id: cacheId,
            fields: {
              name: () => form.name,
              active: () => form.active,
              temporarilyClosed: () => form.temporarilyClosed,
              deprecated: () => form.deprecated,
              circuitBoardId: () => form.circuitBoardId,
              color: () => form.color,
              contractType: () => (form.contractType ? form.contractType : null),
              openStartTime: () => hhmmToIsoStockholm(openStartTime) ?? null,
              openEndTime: () => hhmmToIsoStockholm(openEndTime) ?? null,
              sponsor: (_existing) => (form.sponsorId ? {__typename: 'Sponsor', id: form.sponsorId} : null),
              priceWeight: (_existing) => (form.priceWeight ? {__typename: 'PriceWeight', id: form.priceWeight} : null),
              location: (existing) => {
                const base: Record<string, any> = existing && typeof existing === 'object' ? (existing as any) : {}

                const coords = resolveCoordinates(locationForm.form.lat, locationForm.form.long)

                return {
                  __typename: 'Location',
                  ...base,
                  name: loc.name ?? base.name ?? null,
                  description: loc.description ?? base.description ?? null,
                  municipality: loc.municipality ?? base.municipality ?? null,
                  locality: loc.locality ?? base.locality ?? null,
                  coordinates: coords
                    ? {
                        __typename: 'Coordinates',
                        lat: coords.lat,
                        long: coords.long,
                      }
                    : (base.coordinates ?? null),
                }
              },
            },
          })
        },
      })

      if (result?.data?.editBox?.type === 'Success') {
        showNotification({message: 'Box edited.', severity: 'success'})
        onClose()
      } else {
        showNotification({
          message: resolveErrorMessage(result?.data?.editBox?.type),
          severity: 'error',
        })
      }
    } catch (e) {
      showNotification({message: 'Could not edit box.', severity: 'error'})
      console.log('Box edit error', e, '--- e')
    }
  }

  return (
    <Drawer anchor={'right'} onClose={onClose} open={open}>
      <Box m={2}>
        <Typography variant="h4">Edit box "{box.name}"</Typography>
        <Box mt={2}>
          <form noValidate autoComplete="off">
            <FormSection title="Status" first>
              <CheckboxWithLabel
                label={
                  <LabelWithTooltip
                    label="Active"
                    tooltip="Available to customers in normal operation. When inactive, the box is not shown on the map."
                  />
                }
                labelPlacement={'start'}
                onChange={onActiveChange}
                value={form.active}
              />

              <Box mt={1}>
                <CheckboxWithLabel
                  label={
                    <LabelWithTooltip
                      label="Temporarily closed"
                      tooltip="Box is temporarily unavailable, for example during maintenance. The box marker is shown in grey in the app, but users can still view box details."
                      color={form.temporarilyClosed ? theme.palette.warning.main : undefined}
                    />
                  }
                  labelPlacement={'start'}
                  onChange={onTemporarilyClosedChange}
                  value={form.temporarilyClosed}
                />
              </Box>

              <Box mt={1}>
                <CheckboxWithLabel
                  label={
                    <LabelWithTooltip
                      label="Deprecated"
                      tooltip="Box should no longer be used and may be removed in the future."
                      color={form.deprecated ? theme.palette.error.main : undefined}
                    />
                  }
                  labelPlacement={'start'}
                  onChange={onDeprecatedChange}
                  value={form.deprecated}
                />
              </Box>
            </FormSection>

            <FormSection title="Identity">
              <Box display={'flex'} flexWrap={'wrap'}>
                <Box mr={1} mt={1}>
                  <TextField id="edit-box-name" label="Name" value={form.name} onChange={onNameChange} />
                </Box>

                <Box mr={1} mt={1}>
                  <TextField id="edit-box-circuit-board-id" label="Circuit Board Id" value={form.circuitBoardId} onChange={onCircuitBoardIdChange} />
                </Box>

                <Box mr={1} mt={1}>
                  <BoxColorSelect id={'edit-box-color'} value={form.color} onChange={onColorChange} />
                </Box>
              </Box>
            </FormSection>

            <FormSection title="Availability">
              <Box>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  value={form.alwaysOpen ? 'always' : 'specific'}
                  onChange={onAvailabilityModeChange}
                >
                  <ToggleButton id="edit-box-availability-always" value="always">
                    Always open (24h)
                  </ToggleButton>
                  <ToggleButton id="edit-box-availability-specific" value="specific">
                    Specific hours
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {!form.alwaysOpen && (
                <>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
                    <Box display={'flex'} flexWrap={'wrap'}>
                      <Box mr={1} mt={1}>
                        <TimePicker
                          label="Open start time"
                          ampm={false}
                          value={hhmmToDate(form.openStartTime)}
                          onChange={onOpenStartTimeChange}
                          slotProps={{textField: {id: 'edit-box-open-start-time'}}}
                        />
                      </Box>

                      <Box mr={1} mt={1}>
                        <TimePicker
                          label="Open end time"
                          ampm={false}
                          value={hhmmToDate(form.openEndTime)}
                          onChange={onOpenEndTimeChange}
                          slotProps={{textField: {id: 'edit-box-open-end-time'}}}
                        />
                      </Box>
                    </Box>
                  </LocalizationProvider>

                  <Box mt={1}>
                    <OpeningHoursPreview start={form.openStartTime} end={form.openEndTime} />
                  </Box>
                </>
              )}
            </FormSection>

            <FormSection title="Commercial">
              <Box display={'flex'} flexWrap={'wrap'}>
                <Box mr={1}>
                  <SponsorSelect id={'edit-box-sponsor'} value={form.sponsorId} onChange={onSponsorChange} />
                </Box>
                <Box mr={1}>
                  <PriceWeightSelect id={'edit-box-priceWeight'} value={form.priceWeight} onChange={onPriceWeightChange} />
                </Box>
              </Box>

              <Box mt={2}>
                <Select id="edit-box-contract-type" label="Contract type" value={form.contractType as any} onChange={onContractTypeChange as any}>
                  <MenuItem value="">Not set</MenuItem>
                  <MenuItem value={GQL_ContractType.Care}>{contractTypeLabel(GQL_ContractType.Care)}</MenuItem>
                  <MenuItem value={GQL_ContractType.CarePlus}>{contractTypeLabel(GQL_ContractType.CarePlus)}</MenuItem>
                  <MenuItem value={GQL_ContractType.FullService_1}>{contractTypeLabel(GQL_ContractType.FullService_1)}</MenuItem>
                  <MenuItem value={GQL_ContractType.FullService_2}>{contractTypeLabel(GQL_ContractType.FullService_2)}</MenuItem>
                  <MenuItem value={GQL_ContractType.Firewood}>{contractTypeLabel(GQL_ContractType.Firewood)}</MenuItem>
                  <MenuItem value={GQL_ContractType.Minigolf}>{contractTypeLabel(GQL_ContractType.Minigolf)}</MenuItem>
                </Select>
              </Box>
            </FormSection>

            <FormSection title="Location">
              <EditLocation form={locationForm.form} updateField={locationForm.updateField} />
            </FormSection>
          </form>
        </Box>

        <Box display={'flex'} justifyContent={'space-between'}>
          <Box mt={4}>
            <DeleteBoxButton id={box.id} />
          </Box>

          <Box display={'flex'} justifyContent={'flex-end'} mt={4}>
            <Button onClick={onCancel}>Cancel</Button>
            <Box ml={2} />
            <SaveButton loading={loading} onClick={onSave} />
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}
